import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import type {
  OrdersState,
  OrderItem,
  CurrentOrderState,
  OrderProductSnapshot,
} from "./types";
import type { RestOrder } from "./schemaTypes";
import type { MenuProduct } from "@/types/menu-product";
import type { SelectedOption } from "@/core/options";
import { calculateCartTotal, areProductsEqual } from "./utils";
import { groupToItem } from "./map";
import { itemKey, optionsFingerprint } from "@/core/options/fingerprint";

const initialCurrentOrder: CurrentOrderState = {
  id: undefined,
  brandId: "",
  pointId: "",
  comment: "",
  personsNumber: null,
  type: "PRE_ORDER",
  items: [],
  priceTotal: 0,
  isSyncing: false,
  lastSyncError: null,
  pendingChanges: false,
};

const initialState: OrdersState = {
  currentOrder: initialCurrentOrder,
  loading: false,
  error: null,
  persistedOrderId: undefined,
  pendingRollback: null,
};

/**
 * Захватывает snapshot текущего currentOrder в state.pendingRollback,
 * если ещё нет ожидающего отката. Несколько последовательных оптимистичных
 * мутаций в окне debounce sync саги схлопываются в один цикл — snapshot
 * хранит самое раннее pre-optimistic состояние.
 *
 * Глубина копирования: одноуровневая для items (`items.map(item => ({...item}))`).
 * Достаточно при текущих reducer'ах: они мутируют только `quantity` (примитив)
 * или заменяют целый item через `items[i] = { ...item, ...updates }` (новая ссылка,
 * snapshot не затрагивается). Вложенные поля (`options`, `productSnapshot`,
 * `categories`, `productVariantProperties`) НИГДЕ не мутируются in-place. Если в
 * будущем появится reducer вида `items[i].options.push(...)` — нужно перейти
 * на `structuredClone(state.currentOrder)` или явно расклонировать вложенные
 * массивы здесь.
 */
function captureRollback(state: OrdersState): void {
  if (state.pendingRollback === null) {
    state.pendingRollback = {
      id: state.currentOrder.id,
      brandId: state.currentOrder.brandId,
      pointId: state.currentOrder.pointId,
      comment: state.currentOrder.comment,
      personsNumber: state.currentOrder.personsNumber,
      type: state.currentOrder.type,
      items: state.currentOrder.items.map((item) => ({ ...item })),
      priceTotal: state.currentOrder.priceTotal,
      isSyncing: state.currentOrder.isSyncing,
      lastSyncError: state.currentOrder.lastSyncError,
      pendingChanges: state.currentOrder.pendingChanges,
    };
  }
}

/**
 * АДРЕС СТРОКИ КОРЗИНЫ — `itemKey(item)`, а не productId.
 *
 * До появления опций позиция товара в корзине была ровно одна, и productId
 * работал адресом. Теперь две позиции одного товара с разными наборами
 * законны (спека §7), и productId находит ПЕРВУЮ из них: «+» на второй
 * строке менял бы количество первой, а обнуление второй сносило бы обе
 * (filter выбрасывал все позиции товара сразу).
 *
 * Поэтому payload трёх мутаторов ниже несёт `itemKey` — товар плюс отпечаток
 * набора (`core/options/fingerprint.ts`). У позиции без опций отпечаток
 * пустой, то есть ключ остаётся прежним `productId|`, и корзина, поднятая с
 * сервера, адресуется как раньше.
 */
export const ordersReducer = createSlice({
  name: "orders",
  initialState,
  reducers: {
    // ===== CART OPERATIONS =====

    addPosition: (
      state,
      action: PayloadAction<{
        product: MenuProduct;
        quantity?: number;
        options?: SelectedOption[];
        comment?: string;
      }>
    ) => {
      captureRollback(state);
      const { product, quantity = 1, options = [], comment = "" } = action.payload;

      // Создаем productSnapshot из product
      const productSnapshot: OrderProductSnapshot = {
        id: product.id,
        productId: product.id,
        name: product.name || "",
        imageId: product.images?.[0]?.id ?? null,
        variantProperties:
          product.variantSettings?.productVariantPropertyBinds?.map((bind) => ({
            variantPropertyId: bind.productVariantPropertyId,
            variantPropertyName: "",
            variantPropertyValueId: bind.productVariantPropertyValueId || "",
            variantPropertyValueName: "",
          })) ?? [],
        categories:
          product.categoryBinds?.map((cat) => ({
            categoryId: cat.categoryId,
            categoryName: "",
          })) ?? [],
      };

      // Проверяем, есть ли уже такая же позиция в корзине: товар, варианты И
      // набор опций. Одинаковый товар с разным набором опций — разные позиции.
      const incomingFingerprint = optionsFingerprint(options);
      const existingItemIndex = state.currentOrder.items.findIndex(
        (item) =>
          areProductsEqual(
            item.productSnapshot,
            product,
            optionsFingerprint(item.options) === incomingFingerprint
          )
      );

      if (existingItemIndex !== -1) {
        // Товар уже есть в корзине - увеличиваем количество
        state.currentOrder.items[existingItemIndex].quantity += quantity;
      } else {
        // Товара нет в корзине - добавляем как новую позицию
        const newItem: OrderItem = {
          id: undefined, // Будет сгенерирован при синхронизации или использован из GraphQL
          productId: product.id, // Product ID from GraphQL
          productSnapshot,
          quantity: quantity,
          // Цена ТОВАРА без опций: их прибавляет itemPrice при расчёте.
          // У позиции с сервера (groupToItem) это же поле хранит другую
          // величину — см. комментарий там.
          price: product.pricePoint || 0,
          options,
          comment,
        };

        state.currentOrder.items.push(newItem);
      }

      // Обновляем общую стоимость корзины
      state.currentOrder.priceTotal = calculateCartTotal(
        state.currentOrder.items
      );

      // Устанавливаем pendingChanges для синхронизации
      state.currentOrder.pendingChanges = true;
    },

    removeOrderItem: (
      state,
      action: PayloadAction<{
        itemKey: string;
      }>
    ) => {
      captureRollback(state);
      const { itemKey: targetKey } = action.payload;

      state.currentOrder.items = state.currentOrder.items.filter(
        (item) => itemKey(item) !== targetKey
      );
      state.currentOrder.priceTotal = calculateCartTotal(
        state.currentOrder.items
      );

      state.currentOrder.pendingChanges = true;
    },

    /**
     * ВНИМАНИЕ при появлении потребителя: смена набора опций обязана
     * пересчитать `price` от актуального меню.
     *
     * Сегодня экшен не вызывается ниоткуда, и это единственное, что
     * закрывает дыру: diffToPatch выразит смену набора как itemsRemove плюс
     * itemsAdd, а контракт на такое тело считает цену группы ЗАНОВО. Сохранив
     * замороженную цену снятой позиции, корзина разошлась бы с сервером
     * устойчиво — ответ сервера этого не схлопывает.
     */
    updateOrderItem: (
      state,
      action: PayloadAction<{
        itemKey: string;
        updates: Partial<Pick<OrderItem, "quantity" | "options" | "comment">>;
      }>
    ) => {
      captureRollback(state);
      const { itemKey: targetKey, updates } = action.payload;
      const itemIndex = state.currentOrder.items.findIndex(
        (item) => itemKey(item) === targetKey
      );

      if (itemIndex !== -1) {
        const item = state.currentOrder.items[itemIndex];
        state.currentOrder.items[itemIndex] = {
          ...item,
          ...updates,
        };
        state.currentOrder.priceTotal = calculateCartTotal(
          state.currentOrder.items
        );
      }

      state.currentOrder.pendingChanges = true;
    },

    updateQuantity: (
      state,
      action: PayloadAction<{
        itemKey: string;
        quantity: number;
      }>
    ) => {
      captureRollback(state);
      const { itemKey: targetKey, quantity } = action.payload;

      const itemIndex = state.currentOrder.items.findIndex(
        (item) => itemKey(item) === targetKey
      );

      if (itemIndex !== -1 && quantity > 0) {
        state.currentOrder.items[itemIndex].quantity = quantity;
        state.currentOrder.priceTotal = calculateCartTotal(
          state.currentOrder.items
        );
      }

      state.currentOrder.pendingChanges = true;
    },

    updateOrderComment: (
      state,
      action: PayloadAction<{
        comment: string;
      }>
    ) => {
      captureRollback(state);
      const { comment } = action.payload;

      state.currentOrder.comment = comment;
      state.currentOrder.pendingChanges = true;
    },

    clearOrder: (state) => {
      captureRollback(state);
      // Сохраняем id, brandId, pointId, type для возможности обновления заказа на сервере
      const savedId = state.currentOrder.id;
      const savedBrandId = state.currentOrder.brandId;
      const savedPointId = state.currentOrder.pointId;
      const savedType = state.currentOrder.type;

      // Очищаем корзину, но сохраняем метаданные заказа
      state.currentOrder = {
        ...initialCurrentOrder,
        id: savedId, // Сохраняем id для обновления на сервере
        brandId: savedBrandId || "", // Сохраняем brandId
        pointId: savedPointId || "", // Сохраняем pointId
        type: savedType, // Сохраняем type
        items: [], // Очищаем товары
        priceTotal: 0, // Очищаем сумму
        pendingChanges: true, // Устанавливаем флаг для синхронизации
      };

      // Очищаем persistedOrderId только если нет id заказа
      if (!savedId) {
        state.persistedOrderId = undefined;
      }
    },

    /**
     * Забыть заказ, оставив корзину как есть.
     *
     * clearOrder для этого не годится: при непустом id он СОХРАНЯЕТ и id, и
     * persistedOrderId (ветка `if (!savedId)`), то есть после смены бренда
     * следующий тик синхронизации послал бы PATCH по заказу прежнего бренда — с
     * новым brandId в теле, то есть 404 и снекбар.
     */
    detachOrder: (state) => {
      state.currentOrder.id = undefined;
      state.persistedOrderId = undefined;
    },

    // ===== ORDER SYNC OPERATIONS =====

    syncOrderStart: (state) => {
      state.currentOrder.isSyncing = true;
      state.currentOrder.lastSyncError = null;
      state.currentOrder.pendingChanges = false;
    },

    syncOrderSuccess: (state, action: PayloadAction<RestOrder>) => {
      state.currentOrder.isSyncing = false;
      const order = action.payload;

      // Обновляем currentOrder из ответа сервера (используем логику из loadOrderSuccess)
      state.currentOrder = {
        id: order.id,
        brandId: order.brandId,
        pointId: order.pointId,
        comment: order.comment ?? "",
        personsNumber: order.personsNumber ?? null,
        type: order.type,
        items: order.items.map(groupToItem),
        priceTotal: order.priceTotal,
        isSyncing: false,
        lastSyncError: null,
        pendingChanges: false,
      };

      // Sync прошёл — снимок больше не нужен.
      state.pendingRollback = null;

      // Сохраняем ID заказа для persist
      state.persistedOrderId = order.id;

      // Заказ в state.orders больше не кладётся: серверное состояние живёт в
      // кэше TanStack, а массив вместе со своими селекторами снимается
      // задачей 8 фазы.
    },

    /**
     * Взвести флаг синхронизации, ничего в корзине не трогая.
     *
     * Повтор после ошибки: rollbackOrderChanges гасит pendingChanges вместе с
     * откатом, и без этого экшена повторить было бы нечем. Прежний retrySync
     * диспатчил syncOrderStart — под новой моделью тот, наоборот, ОТМЕНЯЛ бы
     * синхронизацию, снимая флаг.
     */
    markPendingChanges: (state) => {
      state.currentOrder.pendingChanges = true;
    },

    syncOrderError: (state, action: PayloadAction<string>) => {
      state.currentOrder.isSyncing = false;
      state.currentOrder.lastSyncError = action.payload;
      state.currentOrder.pendingChanges = true; // Оставляем для возможности отката
    },

    rollbackOrderChanges: (state) => {
      // Откат на pre-optimistic snapshot. Если snapshot отсутствует —
      // ничего не делаем (нет ожидающего отката).
      if (state.pendingRollback === null) {
        return;
      }
      state.currentOrder = {
        ...state.pendingRollback,
        isSyncing: false,
        lastSyncError: null,
        pendingChanges: false,
      };
      state.pendingRollback = null;
    },

    clearPendingRollback: (state) => {
      // Снимок больше не актуален (мутация не отправилась — early-return в саге).
      // currentOrder остаётся как есть с оптимистичным апдейтом.
      state.pendingRollback = null;
    },
    // ===== ORDER LOADING OPERATIONS =====

    loadOrderSuccess: (state, action: PayloadAction<RestOrder>) => {
      state.loading = false;
      const order = action.payload;

      // Set currentOrder to the loaded order (only needed fields)
      state.currentOrder = {
        id: order.id,
        brandId: order.brandId,
        pointId: order.pointId,
        comment: order.comment ?? "",
        personsNumber: order.personsNumber ?? null,
        type: order.type,
        items: order.items.map(groupToItem),
        priceTotal: order.priceTotal,
        isSyncing: false,
        lastSyncError: null,
        pendingChanges: false,
      };

      // Сохраняем ID заказа для persist
      state.persistedOrderId = order.id;

      // Снимок отката относился к корзине ДО ответа сервера: пережив замену
      // состава, он восстановил бы состояние многократной давности при первой
      // же неудаче синхронизации.
      state.pendingRollback = null;
    },

    // ===== LOAD LAST DRAFT ORDER OPERATIONS =====

    loadLastDraftOrderSuccess: (
      state,
      action: PayloadAction<RestOrder | null>
    ) => {
      state.loading = false;
      const order = action.payload;

      if (order) {
        // Заказ найден: устанавливаем currentOrder из загруженного заказа
        // Используем логику из loadOrderSuccess
        state.currentOrder = {
          id: order.id,
          brandId: order.brandId,
          pointId: order.pointId,
          comment: order.comment ?? "",
          personsNumber: order.personsNumber ?? null,
          type: order.type,
          items: order.items.map(groupToItem),
          priceTotal: order.priceTotal,
          isSyncing: false,
          lastSyncError: null,
          pendingChanges: false,
        };

        // Сохраняем ID заказа для persist
        state.persistedOrderId = order.id;

        // Снимок отката протух вместе с заменённым составом — см.
        // loadOrderSuccess.
        state.pendingRollback = null;
      }
      // Если заказ не найден (null), оставляем currentOrder пустым (не изменяем)
    },

    // ===== GENERAL STATE MANAGEMENT =====

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  // Cart operations
  addPosition,
  removeOrderItem,
  updateOrderItem,
  updateQuantity,
  updateOrderComment,
  clearOrder,
  detachOrder,

  // Order loading
  loadOrderSuccess,

  // Order sync
  syncOrderStart,
  markPendingChanges,
  syncOrderSuccess,
  syncOrderError,
  rollbackOrderChanges,
  clearPendingRollback,

  // Load last draft order
  loadLastDraftOrderSuccess,

  // General state
  setLoading,
  setError,
} = ordersReducer.actions;

// Configuration for Redux persist using localStorage
// Сохраняем только persistedOrderId для восстановления после перезагрузки
const persistConfig = {
  key: "orders",
  storage,
  whitelist: ["persistedOrderId"], // Сохраняем только ID заказа
};

export default persistReducer(persistConfig, ordersReducer.reducer);
